import React, { useState } from "react";
import MultiSelectDropdown from "../../../components/ui/MultiSelectDropdown";
import TimeRangePicker from "../../../components/ui/TimeRangePicker";

const GRANULARITY_OPTIONS = [
  { id: "day", label: "Day" },
  { id: "week", label: "Week" },
  { id: "month", label: "Month" },
  { id: "year", label: "Year" },
];

const ResourceUtilizationKPISettings = ({
  title,
  objectTypes,
  selectedObjectType,
  availableResources,
  configuredResources,
  shiftStart,
  shiftEnd,
  breaks,
  toleranceThreshold,
  ignoreShortTransitions,
  includeNonWorkingDays,
  includeNoDataResources,
  multiResourceMode,
  enabledGranularities,
  onTitleChange,
  onObjectTypeChange,
  onConfiguredResourcesChange,
  onShiftStartChange,
  onShiftEndChange,
  onAddBreak,
  onRemoveBreak,
  onUpdateBreak,
  onToleranceThresholdChange,
  onIgnoreShortTransitionsChange,
  onIncludeNonWorkingDaysChange,
  onIncludeNoDataResourcesChange,
  onMultiResourceModeChange,
  onEnabledGranularitiesChange,
  loading = false,
}) => {
  const [thresholdInput, setThresholdInput] = useState(String(toleranceThreshold ?? 0));
  const toggleGranularity = (id) => {
    if (enabledGranularities.includes(id)) {
      const next = enabledGranularities.filter((g) => g !== id);
      if (next.length > 0) {
        onEnabledGranularitiesChange(next);
      }
      return;
    }
    onEnabledGranularitiesChange([...enabledGranularities, id]);
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Title
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => onTitleChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="Resource Utilization"
          disabled={loading}
        />
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
        <label className="block text-sm font-medium text-gray-700">
          Object Type and Resources
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Object Type *
            </label>
            <select
              value={selectedObjectType}
              onChange={(e) => onObjectTypeChange(e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              disabled={loading}
            >
              <option value="">Select Object Type</option>
              {(objectTypes || []).map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
          <div>
            <MultiSelectDropdown
              label="Configured Resources *"
              options={availableResources.map(String)}
              selectedValues={configuredResources}
              onSelectionChange={onConfiguredResourcesChange}
              placeholder={
                availableResources.length === 0
                  ? "Load data first..."
                  : "Select resources..."
              }
              disabled={loading || availableResources.length === 0}
              itemType="resources"
              explicitDisplayCount={5}
            />
          </div>
        </div>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <TimeRangePicker
          startTime={shiftStart}
          endTime={shiftEnd}
          onStartChange={onShiftStartChange}
          onEndChange={onShiftEndChange}
          label="Shift Schedule *"
          hint="Shift is reused from Total Downtime logic."
          disabled={loading}
        />
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium text-gray-700">
            Breaks
          </label>
          <button
            onClick={onAddBreak}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-primary-600 bg-primary-50 border border-primary-200 rounded-md hover:bg-primary-100"
            disabled={loading}
          >
            Add Break
          </button>
        </div>
        {breaks.length === 0 ? (
          <p className="text-xs text-gray-500 italic">No breaks configured.</p>
        ) : (
          <div className="space-y-2">
            {breaks.map((brk, idx) => (
              <div
                key={idx}
                className="flex items-center gap-2 bg-white border border-gray-200 rounded-md p-2"
              >
                <TimeRangePicker
                  startTime={brk.start}
                  endTime={brk.end}
                  onStartChange={(val) => onUpdateBreak(idx, "start", val)}
                  onEndChange={(val) => onUpdateBreak(idx, "end", val)}
                  startLabel="From"
                  endLabel="To"
                  disabled={loading}
                  className="flex-1"
                />
                <button
                  onClick={() => onRemoveBreak(idx)}
                  className="flex-shrink-0 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                  title="Remove break"
                  disabled={loading}
                >
                  x
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700">
          Multi-Resource Mode
        </label>
        <select
          value={multiResourceMode}
          onChange={(e) => onMultiResourceModeChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          disabled={loading}
        >
          <option value="accumulated">Accumulated</option>
          <option value="concurrent">Concurrent</option>
        </select>
      </div>

      <div>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={ignoreShortTransitions}
            onChange={(e) => onIgnoreShortTransitionsChange(e.target.checked)}
            className="rounded border-gray-300 focus:ring-primary-500"
            style={{ accentColor: "#0066cc" }}
            disabled={loading}
          />
          <span className="text-sm font-medium text-gray-700">
            Ignore short transitions
          </span>
        </label>
      </div>

      {ignoreShortTransitions && (
        <div className="ml-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Transition tolerance (seconds)
          </label>
          <input
            type="number"
            value={thresholdInput}
            onChange={(e) => {
              const raw = e.target.value;
              setThresholdInput(raw);
              if (raw === '') return;
              const parsed = parseInt(raw, 10);
              if (!isNaN(parsed)) {
                onToleranceThresholdChange(Math.max(0, parsed));
              }
            }}
            onBlur={() => {
              const parsed = parseInt(thresholdInput, 10);
              const safe = isNaN(parsed) ? 0 : Math.max(0, parsed);
              setThresholdInput(String(safe));
              onToleranceThresholdChange(safe);
            }}
            min="0"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            disabled={loading}
          />
        </div>
      )}

      <div>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={includeNoDataResources}
            onChange={(e) => onIncludeNoDataResourcesChange(e.target.checked)}
            className="rounded border-gray-300 focus:ring-primary-500"
            style={{ accentColor: "#0066cc" }}
            disabled={loading}
          />
          <span className="text-sm font-medium text-gray-700">
            Count resources without data as full downtime
          </span>
        </label>
        <p className="text-xs text-gray-500 mt-1 ml-6">
          When enabled, selected resources with no records in the dataset are
          treated as 100% down for every analyzed day
        </p>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Enabled Granularities (View Mode)
        </label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {GRANULARITY_OPTIONS.map((option) => {
            const active = enabledGranularities.includes(option.id);
            return (
              <label
                key={option.id}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border text-sm ${
                  active
                    ? "bg-primary-50 border-primary-300"
                    : "bg-white border-gray-300"
                } ${loading ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
              >
                <input
                  type="checkbox"
                  checked={active}
                  onChange={() => toggleGranularity(option.id)}
                  className="rounded border-gray-300 focus:ring-primary-500"
                  style={{ accentColor: "#0066cc" }}
                  disabled={loading}
                />
                <span className="text-gray-700">{option.label}</span>
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ResourceUtilizationKPISettings;
